"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  saveAgentModelOverrideAction,
  resetAgentModelOverrideAction,
} from "@/server-actions/ai-agent-model-config"
import { agentModelOverrideQueryKey } from "@/hooks/queries/ai-agent-model-config"
import { withQueryHandling } from "@/lib/with-query-handling"
import type { AgentOverrideScopeType } from "@/lib/ai/use-cases"

export function useSaveAgentModelOverrideMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      scope_type: AgentOverrideScopeType
      scope_id: string
      primary_model_id: string
      model_config: Record<string, unknown>
      fallback_model_id?: string
      fallback_model_config?: Record<string, unknown>
    }) => {
      const result = await withQueryHandling(saveAgentModelOverrideAction(input), {
        loading: "Saving model override...",
        success: "Model override saved",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: agentModelOverrideQueryKey(variables.scope_type, variables.scope_id),
      })
    },
  })
}

export function useResetAgentModelOverrideMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { scope_type: AgentOverrideScopeType; scope_id: string }) => {
      const result = await withQueryHandling(resetAgentModelOverrideAction(input), {
        loading: "Resetting to default...",
        success: "Reverted to default model",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: agentModelOverrideQueryKey(variables.scope_type, variables.scope_id),
      })
    },
  })
}
